import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import api from '../../utils/api';
import ThemeBackground from '../../components/Layout/ThemeBackground';

interface ReviewRow {
  _id: string;
  rating: number;
  originalText: string;
  publicText?: string;
  moderationStatus: string;
  safeguardingFlag: boolean;
  consentToPublish: boolean;
  submittedAt: string;
  publishedAt?: string;
  rejectionReason?: string;
  moderationNotes?: string;
}

const cellSx = { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.1)' };
const STATUS_TABS = ['awaiting', 'published', 'private', 'rejected', 'withdrawn', 'flagged'] as const;

const ReviewModeration: React.FC = () => {
  const [tab, setTab] = useState<string>('awaiting');
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishTarget, setPublishTarget] = useState<ReviewRow | null>(null);
  const [publishText, setPublishText] = useState('');
  const [rejectTarget, setRejectTarget] = useState<ReviewRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const flagged = useMemo(() => new URLSearchParams(window.location.search).get('flagged') === '1', []);
  useEffect(() => { if (flagged) setTab('flagged'); }, [flagged]);

  const load = async (status: string) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/verified-reviews/moderation?status=${status}`);
      setReviews(res.data.reviews || []);
    } catch { setReviews([]); }
    setLoading(false);
  };
  useEffect(() => { load(tab); }, [tab]);

  const act = async (fn: () => Promise<any>) => {
    setBusy(true);
    try { await fn(); await load(tab); } finally { setBusy(false); }
  };

  return (
    <ThemeBackground>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" sx={{ color: 'white', mb: 3 }}>Verified Client Reviews</Typography>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          sx={{ mb: 3, '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)' },
            '& .Mui-selected': { color: 'white' }, '& .MuiTabs-indicator': { backgroundColor: '#64b5f6' } }}
        >
          {STATUS_TABS.map(s => <Tab key={s} value={s} label={s === 'flagged' ? 'Safeguarding' : s.charAt(0).toUpperCase() + s.slice(1)} />)}
        </Tabs>

        <Table size="small" sx={{ background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
          <TableHead>
            <TableRow>{['Rating', 'Review', 'Status', 'Submitted', ''].map(h =>
              <TableCell key={h} sx={{ ...cellSx, fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} sx={cellSx}>Loading…</TableCell></TableRow>
            ) : reviews.length === 0 ? (
              <TableRow><TableCell colSpan={5} sx={cellSx}>Nothing in this queue</TableCell></TableRow>
            ) : reviews.map(r => (
              <TableRow key={r._id}>
                <TableCell sx={cellSx}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</TableCell>
                <TableCell sx={{ ...cellSx, maxWidth: 420 }}>
                  <Box sx={{ whiteSpace: 'pre-wrap' }}>{r.publicText || r.originalText}</Box>
                  {r.safeguardingFlag && (
                    <Chip size="small" label="Safeguarding flag" color="error" sx={{ mt: 1 }} />
                  )}
                  {r.rejectionReason && (
                    <Box sx={{ mt: 0.5, fontSize: '0.8rem', opacity: 0.7 }}>Rejected: {r.rejectionReason}</Box>
                  )}
                </TableCell>
                <TableCell sx={cellSx}>{r.moderationStatus}{!r.consentToPublish ? ' · private' : ''}</TableCell>
                <TableCell sx={cellSx}>{new Date(r.submittedAt).toLocaleDateString('en-GB')}</TableCell>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {r.moderationStatus === 'awaiting' && r.consentToPublish && (
                      <Button size="small" variant="contained" disabled={busy}
                        onClick={() => { setPublishTarget(r); setPublishText(r.originalText); }}>
                        Publish
                      </Button>
                    )}
                    {r.moderationStatus === 'published' && (
                      <Button size="small" variant="outlined" disabled={busy}
                        onClick={() => act(() => api.post(`/api/admin/verified-reviews/moderation/${r._id}/unpublish`))}>
                        Unpublish
                      </Button>
                    )}
                    {['awaiting', 'published'].includes(r.moderationStatus) && (
                      <Button size="small" color="error" variant="outlined" disabled={busy}
                        onClick={() => { setRejectTarget(r); setRejectReason(''); }}>
                        Reject
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!publishTarget} onClose={() => setPublishTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Publish review{publishTarget?.safeguardingFlag ? ' (safeguarding-flagged)' : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Edit to redact identifying details only - sentiment must not change.
          </Typography>
          <TextField multiline fullWidth minRows={4} value={publishText}
            onChange={e => setPublishText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => act(async () => {
            await api.post(`/api/admin/verified-reviews/moderation/${publishTarget!._id}/publish`, {
              publicText: publishText,
              confirmSafeguarding: publishTarget?.safeguardingFlag || undefined
            });
            setPublishTarget(null);
          })}>Publish</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject review</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason (required)" value={rejectReason}
            onChange={e => setRejectReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy || !rejectReason.trim()}
            onClick={() => act(async () => {
              await api.post(`/api/admin/verified-reviews/moderation/${rejectTarget!._id}/reject`, { reason: rejectReason });
              setRejectTarget(null);
            })}>Reject</Button>
        </DialogActions>
      </Dialog>
    </ThemeBackground>
  );
};

export default ReviewModeration;
